// BUILD-046 cycle C (Edit 3): the searchable Customer choice of the toolbar's Filter popover. It replaces the native drop-down of every customer.
//
// The popover shows: the name of the control, the two quick choices (All customers, No customer), a search box (role=combobox), and a short hint. Matches are listed ONLY after
// something is typed (at least one character), at most eight, with "Showing 8 of N. Keep typing to narrow." when more match. Arrow keys move through the matches,
// Enter chooses the highlighted one, Escape closes the list (a second Escape closes the popover), and Tab only leaves the field: it NEVER chooses a customer.
// The sentence under the box says which tasks the customers come from (the loaded ones) and never claims to search every task.
// All rules (the options, the search, the wording, the filter a choice stands for) are in customerFilterModel.ts; the key rules are customerFieldModel.decideCustomerKey; this file draws them.
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { CUSTOMER_FILTER_LABEL, CUSTOMER_QUICK_CHOICES, describeCustomerPicker, customerChoiceNote, customerFilterFromOption, customerFilterFromQuick, customerFilterSummary, searchCustomerOptions } from './customerFilterModel';
import type { CustomerChoiceOption, CustomerFilter } from './customerFilterModel';
import { decideCustomerKey, customerOptionId } from './customerFieldModel';
import { closeList, initialCombo, typeQuery } from './peopleComboboxModel';
import type { ComboState } from './peopleComboboxModel';
import { stripTestMarker } from './displayNames';

export interface CustomerPickerControl {
  /** The id prefix, unique per view (the search box is `${idPrefix}-customer`). */
  idPrefix: string;
  /** The filter now: `any`, `none`, or one customer (the stable id is the value). */
  filter: CustomerFilter;
  /** The customers on the rows the view has loaded (customerFilterModel.customerChoicesOf). */
  options: readonly CustomerChoiceOption[];
  /** Where the customers come from (customerFilterModel.customerFilterScopeText). */
  scopeText: string;
  onChange: (next: CustomerFilter) => void;
}

const QUICK_BASE = 'rounded-full border px-3 py-1 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]';

export function CustomerFilterPicker({ idPrefix, filter, options, scopeText, onChange }: CustomerPickerControl) {
  const [combo, setCombo] = useState<ComboState>(initialCombo());
  // What was just chosen, said once in the polite line when the list is closed ("Filtering by Smith Residence.").
  const [chosenNote, setChosenNote] = useState('');
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputId = `${idPrefix}-customer`;
  const labelId = `${inputId}-label`;
  const listboxId = `${inputId}-listbox`;
  const moreId = `${inputId}-more`;
  const scopeId = `${inputId}-scope`;

  const outcome = searchCustomerOptions(options, combo.query);
  const view = describeCustomerPicker(outcome);
  const matches = outcome.matches;
  const showList = combo.open && outcome.status !== 'idle';
  const active = showList && combo.active >= 0 && combo.active < matches.length ? matches[combo.active] : null;
  const describedBy = [scopeText !== '' ? scopeId : undefined, showList && view.moreNote !== null ? moreId : undefined].filter(Boolean).join(' ');
  // Browser run (cycle C): eight two-line matches are taller than the list's own scroll area, so the arrow keys could highlight a match that was out of sight. The highlighted
  // match is brought into view (the list, and the page behind it, scroll only as far as needed).
  const activeCustomerId = active !== null ? active.customerId : null;
  useEffect(() => {
    if (activeCustomerId === null) return;
    document.getElementById(customerOptionId(inputId, activeCustomerId))?.scrollIntoView?.({ block: 'nearest' });
  }, [activeCustomerId, inputId]);

  function choose(customerId: string): void {
    const next = customerFilterFromOption(matches.find((m) => m.customerId === customerId));
    if (next === null) return;
    onChange(next);
    setChosenNote(`Filtering by ${customerFilterSummary(next)}.`);
    setCombo(initialCombo());
  }

  function quick(value: 'any' | 'none'): void {
    const next = customerFilterFromQuick(value);
    onChange(next);
    setChosenNote(value === 'any' ? 'Showing all customers.' : 'Showing tasks with no customer.');
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    // With no list on screen (nothing typed) Escape is the popover's own: it closes the popover.
    if (event.key === 'Escape' && !showList) {
      if (combo.open) setCombo((state) => closeList(state));
      return;
    }
    const decision = decideCustomerKey(event.key, combo, matches);
    if (decision.handled) event.preventDefault();
    // The Escape that closes the list is not also the popover's Escape.
    if (decision.stop) event.stopPropagation();
    if (decision.choose !== null) {
      choose(decision.choose);
      return;
    }
    if (decision.state !== combo) setCombo(decision.state);
  }

  return (
    <div ref={wrapRef} data-testid="customer-filter">
      <p id={labelId} className="mb-1 text-xs font-semibold text-gray-700">
        {CUSTOMER_FILTER_LABEL}
      </p>
      <div role="group" aria-label={`${CUSTOMER_FILTER_LABEL} quick choices`} className="mb-2 flex flex-wrap gap-2">
        {CUSTOMER_QUICK_CHOICES.map((choice) => {
          const selected = filter.kind === choice.value;
          return (
            <button
              key={choice.value}
              type="button"
              aria-pressed={selected}
              onClick={() => quick(choice.value)}
              className={`${QUICK_BASE} ${selected ? 'border-[#2a7fb8] bg-sky-100 text-[#14537d]' : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'}`}
              data-testid={`${inputId}-quick-${choice.value}`}
            >
              {choice.label}
            </button>
          );
        })}
      </div>
      <div className="relative">
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-labelledby={labelId}
          aria-expanded={showList}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={active !== null ? customerOptionId(inputId, active.customerId) : undefined}
          aria-describedby={describedBy}
          autoComplete="off"
          value={combo.query}
          placeholder="Search by customer name"
          onChange={(event) => {
            setChosenNote('');
            setCombo((state) => typeQuery(state, event.target.value));
          }}
          onKeyDown={onKeyDown}
          onBlur={(event) => {
            // Moving into the list keeps it; leaving the picker closes it. The typed text stays.
            const next = event.relatedTarget;
            if (!(next instanceof Node && wrapRef.current !== null && wrapRef.current.contains(next))) setCombo((state) => closeList(state));
          }}
          className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
        />
        {/* The listing is part of the panel (not a floating layer), so the popover never clips it. */}
        {showList && (
          <div className="mt-1 rounded-md border border-gray-200 bg-white" onMouseDown={(event) => event.preventDefault()}>
            <ul id={listboxId} role="listbox" aria-label="Customers" className="max-h-72 overflow-y-auto py-1" data-testid={`${inputId}-listbox`}>
              {matches.map((customer, index) => {
                const note = customerChoiceNote(customer);
                const detail = stripTestMarker((customer as { place?: unknown }).place);
                const current = filter.kind === 'customer' && filter.customerId === customer.customerId;
                return (
                  <li
                    key={customer.customerId}
                    id={customerOptionId(inputId, customer.customerId)}
                    role="option"
                    aria-selected={current}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => choose(customer.customerId)}
                    onMouseMove={() => setCombo((state) => (state.active === index ? state : { ...state, active: index }))}
                    className={`cursor-pointer select-none px-3 py-1.5 text-sm text-gray-900 ${index === combo.active ? 'bg-sky-100' : current ? 'bg-sky-50 hover:bg-sky-100' : 'hover:bg-gray-50'}`}
                  >
                    <span className="block break-words">{stripTestMarker(customer.label)}</span>
                    {(note !== null || detail !== '') && <span className="block text-xs text-gray-500">{[detail, note].filter((part) => part !== '' && part !== null).join(' \u00b7 ')}</span>}
                  </li>
                );
              })}
              {matches.length === 0 && (
                <li role="presentation" className="px-3 py-2 text-sm text-gray-600">
                  {view.emptyText}
                </li>
              )}
            </ul>
            {view.moreNote !== null && (
              <p id={moreId} className="border-t border-gray-100 px-3 py-1 text-xs text-gray-700" data-testid={`${inputId}-more`}>
                {view.moreNote}
              </p>
            )}
          </div>
        )}
      </div>
      <div role="status" aria-live="polite" className="sr-only">
        {showList ? view.announcement : chosenNote}
      </div>
      {scopeText !== '' && (
        <p id={scopeId} className="mt-2 text-xs text-gray-600" data-testid={`${inputId}-scope`}>
          {scopeText}
        </p>
      )}
    </div>
  );
}
